// ═══════════════════════════════════════════════════════════════
// Vue Router — routes publiques (auth) + protégées (app)
// ═══════════════════════════════════════════════════════════════
// Guard global :
//   - meta.requiresAuth + !isAuthenticated → redirect /login
//   - meta.guestOnly + isAuthenticated → redirect /dashboard
// Le store auth doit avoir terminé son bootstrap() avant le 1er guard ;
// App.vue garde un splash tant que bootstrapping=true.
// ═══════════════════════════════════════════════════════════════

import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { tenantHasFeature } from '@/lib/features';

const routes = [
  { path: '/', redirect: { name: 'dashboard' } },

  // ─── Auth (public) ─────────────────────────────────────
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/Login.vue'),
    meta: { guestOnly: true, layout: 'auth' },
  },
  {
    path: '/login/mfa',
    name: 'mfa-challenge',
    component: () => import('@/views/auth/MfaChallenge.vue'),
    meta: { guestOnly: true, layout: 'auth' },
  },
  // Inscription publique (auto-trial self-service, sans carte).
  {
    path: '/signup',
    name: 'signup',
    component: () => import('@/views/auth/Signup.vue'),
    meta: { guestOnly: true, layout: 'auth' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/auth/ForgotPassword.vue'),
    meta: { guestOnly: true, layout: 'auth' },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/auth/ResetPassword.vue'),
    meta: { guestOnly: true, layout: 'auth' },
  },
  {
    path: '/accept-invitation',
    name: 'accept-invitation',
    component: () => import('@/views/auth/AcceptInvitation.vue'),
    meta: { guestOnly: true, layout: 'auth' },
  },
  {
    path: '/verify-email',
    name: 'verify-email',
    component: () => import('@/views/auth/VerifyEmail.vue'),
    meta: { layout: 'auth' },
  },
  {
    path: '/auth/callback/google',
    name: 'oauth-callback-google',
    component: () => import('@/views/auth/OauthCallbackGoogle.vue'),
    meta: { layout: 'auth' },
  },
  {
    // Gate MFA obligatoire (tenant en politique REQUIRED) : page pleine sans
    // chrome applicatif, l'utilisateur doit activer une méthode avant d'entrer.
    path: '/mfa-required',
    name: 'mfa-required',
    component: () => import('@/views/auth/MfaRequired.vue'),
    meta: { requiresAuth: true, layout: 'auth' },
  },

  // ─── App (auth) ────────────────────────────────────────
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/dashboard/Dashboard.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/suppliers',
    name: 'suppliers',
    component: () => import('@/views/suppliers/SuppliersList.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/suppliers/import',
    name: 'suppliers-import',
    component: () => import('@/views/suppliers/SuppliersImport.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/ingredients',
    name: 'ingredients',
    component: () => import('@/views/ingredients/IngredientsList.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/support',
    name: 'support',
    component: () => import('@/views/support/SupportList.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/demo-caisse',
    name: 'demo-caisse',
    component: () => import('@/views/demo/DemoCaisse.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/launch-demo',
    name: 'launch-demo',
    component: () => import('@/views/demo/LaunchDemo.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/support/:id',
    name: 'support-detail',
    component: () => import('@/views/support/SupportDetail.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/ingredients/import',
    name: 'ingredients-import',
    component: () => import('@/views/ingredients/IngredientsImport.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/ingredients/establishment-settings/import',
    name: 'ingredients-establishment-settings-import',
    component: () => import('@/views/ingredients/EstablishmentSettingsImport.vue'),
    meta: { requiresAuth: true, requiresFeature: 'establishment-stock-settings', requiresRole: 'MANAGER' },
  },
  {
    // Articles tenus par établissement (import/export « une colonne par site »).
    path: '/ingredients/stocking/import',
    name: 'ingredients-stocking-import',
    component: () => import('@/views/ingredients/EstablishmentStockingImport.vue'),
    meta: { requiresAuth: true, requiresRole: 'MANAGER' },
  },
  {
    path: '/recipes',
    name: 'recipes',
    component: () => import('@/views/recipes/RecipesList.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/recipes/new',
    name: 'recipe-new',
    component: () => import('@/views/recipes/RecipeNew.vue'),
    meta: { requiresAuth: true },
  },
  {
    // Import CSV — chemin littéral AVANT /recipes/:id pour ne pas être
    // matché comme un id de recette.
    path: '/recipes/import',
    name: 'recipes-import',
    component: () => import('@/views/recipes/RecipesImport.vue'),
    meta: { requiresAuth: true, requiresRole: 'MANAGER' },
  },
  {
    path: '/recipes/:id',
    name: 'recipe-edit',
    component: () => import('@/views/recipes/RecipeEdit.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/purchase-orders',
    name: 'purchase-orders',
    component: () => import('@/views/purchase-orders/PurchaseOrdersList.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/purchase-orders/reorder',
    name: 'reorder',
    component: () => import('@/views/purchase-orders/ReorderSuggestions.vue'),
    meta: { requiresAuth: true, requiresFeature: 'reorder' },
  },
  {
    path: '/supplier-invoices',
    name: 'supplier-invoices',
    component: () => import('@/views/purchase-orders/SupplierInvoices.vue'),
    meta: { requiresAuth: true, requiresFeature: 'ocr', requiresRole: 'MANAGER' },
  },
  {
    path: '/purchase-orders/new',
    name: 'purchase-order-new',
    component: () => import('@/views/purchase-orders/PurchaseOrderNew.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/purchase-orders/:id',
    name: 'purchase-order-detail',
    component: () => import('@/views/purchase-orders/PurchaseOrderDetail.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/stock',
    name: 'stock',
    component: () => import('@/views/stock/StockList.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/stock/movements',
    name: 'stock-movements',
    component: () => import('@/views/stock/StockMovements.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/stock/lots',
    name: 'stock-lots',
    component: () => import('@/views/stock/LotsList.vue'),
    meta: { requiresAuth: true, requiresFeature: 'lots' },
  },
  {
    path: '/transfers',
    name: 'transfers',
    component: () => import('@/views/transfers/TransfersList.vue'),
    meta: { requiresAuth: true, requiresFeature: 'transfers' },
  },
  {
    path: '/transfers/new',
    name: 'transfer-new',
    component: () => import('@/views/transfers/TransferNew.vue'),
    meta: { requiresAuth: true, requiresFeature: 'transfers' },
  },
  {
    // Édition d'un transfert en brouillon (réutilise le formulaire TransferNew)
    path: '/transfers/:id/edit',
    name: 'transfer-edit',
    component: () => import('@/views/transfers/TransferNew.vue'),
    meta: { requiresAuth: true, requiresFeature: 'transfers' },
  },
  {
    path: '/transfers/:id',
    name: 'transfer-detail',
    component: () => import('@/views/transfers/TransferDetail.vue'),
    meta: { requiresAuth: true, requiresFeature: 'transfers' },
  },
  {
    // Inbox labo des commandes internes (feature 'internal-orders')
    path: '/internal-orders',
    name: 'internal-orders',
    component: () => import('@/views/internal-orders/InternalOrdersInbox.vue'),
    meta: { requiresAuth: true, requiresFeature: 'internal-orders' },
  },
  {
    path: '/production',
    name: 'production',
    component: () => import('@/views/production/ProductionList.vue'),
    meta: { requiresAuth: true, requiresFeature: 'production' },
  },
  {
    path: '/stock/put-away',
    name: 'stock-put-away',
    component: () => import('@/views/stock/PutAwayView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'stock-zones' },
  },
  {
    path: '/transformations',
    name: 'transformations',
    component: () => import('@/views/transformations/TransformationsList.vue'),
    meta: { requiresAuth: true, requiresFeature: 'butchery' },
  },
  {
    path: '/transformations/new',
    name: 'transformation-new',
    component: () => import('@/views/transformations/TransformationDetail.vue'),
    meta: { requiresAuth: true, requiresFeature: 'butchery', requiresRole: 'MANAGER' },
  },
  {
    path: '/transformations/templates',
    name: 'transformation-templates',
    component: () => import('@/views/transformations/TransformationTemplates.vue'),
    meta: { requiresAuth: true, requiresFeature: 'butchery', requiresRole: 'MANAGER' },
  },
  {
    path: '/transformations/schedules',
    name: 'transformation-schedules',
    component: () => import('@/views/transformations/TransformationSchedules.vue'),
    meta: { requiresAuth: true, requiresFeature: 'butchery', requiresRole: 'MANAGER' },
  },
  {
    path: '/transformations/trends',
    name: 'transformation-trends',
    component: () => import('@/views/transformations/TransformationTrends.vue'),
    meta: { requiresAuth: true, requiresFeature: 'butchery', requiresRole: 'MANAGER' },
  },
  {
    path: '/transformations/:id',
    name: 'transformation-detail',
    component: () => import('@/views/transformations/TransformationDetail.vue'),
    meta: { requiresAuth: true, requiresFeature: 'butchery' },
  },
  {
    path: '/production/new',
    name: 'production-new',
    component: () => import('@/views/production/ProductionNew.vue'),
    meta: { requiresAuth: true, requiresFeature: 'production' },
  },
  {
    path: '/production/:id',
    name: 'production-detail',
    component: () => import('@/views/production/ProductionDetail.vue'),
    meta: { requiresAuth: true, requiresFeature: 'production' },
  },
  {
    path: '/reports/stock-valuation',
    name: 'stock-valuation',
    component: () => import('@/views/reports/StockValuation.vue'),
    meta: { requiresAuth: true, requiresFeature: 'stock-valuation' },
  },
  {
    path: '/reports/food-cost',
    name: 'reports-food-cost',
    component: () => import('@/views/reports/FoodCostDashboard.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reports/evolution',
    name: 'reports-evolution',
    component: () => import('@/views/reports/ReportsEvolution.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reports/losses',
    name: 'reports-losses',
    component: () => import('@/views/reports/LossesReport.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reports/consumption-by-recipe',
    name: 'reports-consumption-by-recipe',
    component: () => import('@/views/reports/ConsumptionByRecipe.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reports/exports',
    name: 'reports-exports',
    component: () => import('@/views/reports/ExportsCenter.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reports/butchery',
    name: 'reports-butchery',
    component: () => import('@/views/reports/ButcheryReport.vue'),
    meta: { requiresAuth: true, requiresFeature: 'butchery' },
  },
  {
    path: '/reports/production',
    name: 'reports-production',
    component: () => import('@/views/reports/ProductionReport.vue'),
    meta: { requiresAuth: true, requiresFeature: 'production' },
  },
  {
    path: '/reports/transfers',
    name: 'reports-transfers',
    component: () => import('@/views/reports/TransferReport.vue'),
    meta: { requiresAuth: true, requiresFeature: 'transfers' },
  },
  {
    path: '/reports/dlc',
    name: 'reports-dlc',
    component: () => import('@/views/reports/DlcReport.vue'),
    meta: { requiresAuth: true, requiresFeature: 'lots' },
  },
  {
    path: '/reports/margin',
    name: 'reports-margin',
    component: () => import('@/views/reports/MarginReport.vue'),
    meta: { requiresAuth: true, requiresFeature: 'selling-price' },
  },
  {
    path: '/reports/stock-rotation',
    name: 'reports-stock-rotation',
    component: () => import('@/views/reports/StockRotationReport.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/cashmag/mapping',
    name: 'cashmag-mapping',
    component: () => import('@/views/cashmag/CashmagMapping.vue'),
    meta: { requiresAuth: true, requiresFeature: 'cashmag' },
  },
  {
    path: '/cashmag/dashboard',
    name: 'cashmag-dashboard',
    component: () => import('@/views/cashmag/CashmagDashboard.vue'),
    meta: { requiresAuth: true, requiresFeature: 'cashmag' },
  },
  {
    path: '/cashmag/deferred',
    name: 'cashmag-deferred',
    component: () => import('@/views/cashmag/CashmagDeferred.vue'),
    meta: { requiresAuth: true, requiresFeature: 'cashmag' },
  },
  {
    path: '/inventories',
    name: 'inventories',
    component: () => import('@/views/inventories/InventoriesList.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/inventories/:id',
    name: 'inventory-detail',
    component: () => import('@/views/inventories/InventoryDetail.vue'),
    meta: { requiresAuth: true },
  },
  {
    // E7 : mode terrain mobile (plein écran, scan caméra)
    path: '/inventories/:id/mobile',
    name: 'inventory-mobile',
    component: () => import('@/views/inventories/InventoryMobile.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/settings/profile',
    name: 'settings-profile',
    component: () => import('@/views/settings/SettingsProfile.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/settings/tenant',
    name: 'settings-tenant',
    component: () => import('@/views/settings/SettingsTenant.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/settings/plan',
    name: 'settings-plan',
    component: () => import('@/views/settings/SettingsPlan.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/settings/notifications',
    name: 'settings-notifications',
    component: () => import('@/views/settings/SettingsNotifications.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/settings/team',
    name: 'settings-team',
    component: () => import('@/views/settings/SettingsTeam.vue'),
    meta: { requiresAuth: true, requiresRole: 'ADMIN' },
  },
  {
    path: '/settings/haccp',
    name: 'settings-haccp',
    component: () => import('@/views/settings/SettingsHaccp.vue'),
    meta: { requiresAuth: true, requiresFeature: 'haccp' },
  },
  {
    path: '/recalls',
    name: 'recalls',
    component: () => import('@/views/recalls/RecallsList.vue'),
    meta: { requiresAuth: true, requiresFeature: 'recalls' },
  },
  {
    path: '/settings/inventory-schedules',
    name: 'settings-inventory-schedules',
    component: () => import('@/views/settings/SettingsInventorySchedules.vue'),
    meta: { requiresAuth: true, requiresRole: 'MANAGER' },
  },
  {
    path: '/settings/production-schedules',
    name: 'settings-production-schedules',
    component: () => import('@/views/settings/SettingsProductionSchedules.vue'),
    meta: { requiresAuth: true, requiresRole: 'MANAGER', requiresFeature: 'production' },
  },

  // ─── Public Magic Link HACCP (sans auth) ───────────────
  {
    path: '/m/h/:token',
    name: 'haccp-magic-link',
    component: () => import('@/views/HaccpMagicLink.vue'),
    meta: { layout: 'public', public: true },
  },
  // ─── Validation d'une commande au-dessus du seuil (lien email, sans compte) ──
  {
    path: '/m/approve/:token',
    name: 'purchase-order-approval',
    component: () => import('@/views/PurchaseOrderApproval.vue'),
    meta: { layout: 'public', public: true },
  },
  // ─── Lien opérateur d'inventaire (comptage sans compte) ──
  {
    path: '/m/inv/:token',
    name: 'inventory-public-count',
    component: () => import('@/views/InventoryPublicCount.vue'),
    meta: { layout: 'public', public: true },
  },

  // ─── Backoffice SUPERADMIN ─────────────────────────────
  {
    path: '/superadmin',
    name: 'superadmin',
    component: () => import('@/views/superadmin/SuperadminTenants.vue'),
    meta: { requiresAuth: true, requiresSuperadmin: true, layout: 'superadmin' },
  },
  {
    path: '/superadmin/tenants/new',
    name: 'superadmin-tenant-new',
    component: () => import('@/views/superadmin/SuperadminTenantNew.vue'),
    meta: { requiresAuth: true, requiresSuperadmin: true, layout: 'superadmin' },
  },
  {
    path: '/superadmin/whatsapp-billing',
    name: 'superadmin-whatsapp-billing',
    component: () => import('@/views/superadmin/SuperadminWhatsappBilling.vue'),
    meta: { requiresAuth: true, requiresSuperadmin: true, layout: 'superadmin' },
  },
  {
    path: '/superadmin/resellers',
    name: 'superadmin-resellers',
    component: () => import('@/views/superadmin/SuperadminResellers.vue'),
    meta: { requiresAuth: true, requiresSuperadmin: true, layout: 'superadmin' },
  },
  {
    path: '/superadmin/resellers-billing',
    name: 'superadmin-resellers-billing',
    component: () => import('@/views/superadmin/SuperadminResellersBilling.vue'),
    meta: { requiresAuth: true, requiresSuperadmin: true, layout: 'superadmin' },
  },
  // ─── Espace revendeur (Phase R) ──────────────────────────
  {
    path: '/reseller',
    name: 'reseller',
    component: () => import('@/views/reseller/ResellerAccounts.vue'),
    meta: { requiresAuth: true, requiresReseller: true, layout: 'reseller' },
  },
  {
    path: '/reseller/billing',
    name: 'reseller-billing',
    component: () => import('@/views/reseller/ResellerBilling.vue'),
    meta: { requiresAuth: true, requiresReseller: true, layout: 'reseller' },
  },
  {
    path: '/superadmin/tenants/:id',
    name: 'superadmin-tenant-detail',
    component: () => import('@/views/superadmin/SuperadminTenantDetail.vue'),
    meta: { requiresAuth: true, requiresSuperadmin: true, layout: 'superadmin' },
  },

  // ─── Catch-all ─────────────────────────────────────────
  { path: '/:pathMatch(.*)*', redirect: { name: 'dashboard' } },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  // Pendant le bootstrap initial, on laisse passer — App.vue gère le splash
  if (auth.bootstrapping) return true;

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { next: to.fullPath } };
  }
  if (to.meta.guestOnly && auth.isAuthenticated) {
    // Connecté qui tape /login → redirige vers son espace
    if (auth.isSuperadmin) return { name: 'superadmin' };
    if (auth.isReseller && !auth.isImpersonating) return { name: 'reseller' };
    return { name: 'dashboard' };
  }
  if (to.meta.requiresSuperadmin && !auth.isSuperadmin) {
    return { name: 'dashboard' };
  }
  // Espace revendeur : réservé au RESELLER (hors impersonation).
  if (to.meta.requiresReseller && !(auth.isReseller && !auth.isImpersonating)) {
    return auth.isSuperadmin ? { name: 'superadmin' } : { name: 'dashboard' };
  }
  // Un RESELLER hors impersonation n'a pas de contexte tenant → on l'empêche de
  // visiter les pages tenant (il agit via son espace ou via impersonation).
  if (
    auth.isAuthenticated
    && auth.isReseller
    && !auth.isImpersonating
    && to.meta.requiresAuth
    && !to.meta.requiresReseller
  ) {
    return { name: 'reseller' };
  }
  // Mode terrain mobile ouvert via QR par un SUPERADMIN qui était en
  // impersonation sur son PC : le QR embarque ?t=<tenantId>. On rétablit
  // l'impersonation sur ce nouvel appareil AVANT de monter la page (sinon
  // la requête inventaire partirait sans contexte tenant). Sans danger pour
  // un non-SUPERADMIN : l'endpoint impersonate est gated requiresSuperadmin.
  if (
    to.name === 'inventory-mobile'
    && to.query.t
    && auth.isAuthenticated
    && auth.isSuperadmin
    && !auth.isImpersonating
  ) {
    try {
      await auth.enterImpersonation(String(to.query.t));
    } catch {
      return { name: 'superadmin' };
    }
  }
  // Bouton « Démo » (SUPERADMIN/RESELLER) : ouvre /launch-demo?id=<tenantId>
  // dans un nouvel onglet → on impersonne le compte de démo DANS le guard (avant
  // montage), puis on bascule sur son dashboard. Une seule charge de page (pas de
  // rechargement) → pas de conflit de rotation de refresh token entre onglets.
  if (to.name === 'launch-demo') {
    if (auth.isImpersonating) return { name: 'dashboard' };
    if (auth.isAuthenticated && (auth.isSuperadmin || auth.isReseller) && to.query.id) {
      try {
        await auth.enterImpersonation(String(to.query.id));
        return { name: 'dashboard' };
      } catch {
        return { name: auth.isSuperadmin ? 'superadmin' : 'reseller' };
      }
    }
    // Pas (encore) authentifié → on laisse LaunchDemo afficher un fallback.
  }
  // Un SUPERADMIN sans impersonation n'a pas de contexte tenant : on
  // l'empêche de visiter les pages tenant (dashboard, ingrédients, etc.)
  // qui appelleraient des endpoints filtrés par tenantId.
  // Exception : pendant l'impersonation, le SUPERADMIN agit AU NOM d'un
  // tenant — on doit le laisser accéder aux pages tenant normalement.
  if (
    auth.isAuthenticated
    && auth.isSuperadmin
    && !auth.isImpersonating
    && to.meta.requiresAuth
    && !to.meta.requiresSuperadmin
  ) {
    return { name: 'superadmin' };
  }
  // Gate MFA obligatoire : tenant en politique REQUIRED + user non inscrit →
  // redirection vers la configuration MFA AVANT toute page applicative. Le staff
  // et l'impersonation sont exclus (cf. auth.mfaRequired). À l'inverse, si le MFA
  // n'est plus requis, la page gate renvoie vers le dashboard.
  if (auth.mfaRequired && to.meta.requiresAuth && to.name !== 'mfa-required') {
    return { name: 'mfa-required' };
  }
  if (to.name === 'mfa-required' && !auth.mfaRequired) {
    return auth.isAuthenticated ? { name: 'dashboard' } : { name: 'login' };
  }
  // Routes gated par une feature : redirige vers le dashboard si désactivée
  if (to.meta.requiresFeature && !tenantHasFeature(auth.tenant, to.meta.requiresFeature)) {
    return { name: 'dashboard' };
  }
  // Routes gated par un rôle minimum (hiérarchie ADMIN > MANAGER > OPERATOR).
  // requiresRole: 'MANAGER' autorise ADMIN + MANAGER ; requiresRole: 'ADMIN' seul ADMIN.
  // SUPERADMIN passe partout (qu'il soit impersonating ou non).
  if (to.meta.requiresRole && !auth.isSuperadmin) {
    const HIER = { OPERATOR: 1, MANAGER: 2, ADMIN: 3 };
    const userLevel = HIER[auth.user?.role] || 0;
    const minLevel = HIER[to.meta.requiresRole] || 0;
    if (userLevel < minLevel) return { name: 'dashboard' };
  }
  // Enforce des modules user : empêche l'accès direct par URL aux modules
  // non cochés (saisir /recipes alors que recipes n'est pas dans user.modules).
  // ADMIN + SUPERADMIN voient tout (gèrent la matrice).
  // Si user.modules est vide → on autorise (défaut rôle, pas de restriction).
  if (
    auth.isAuthenticated
    && !auth.isSuperadmin
    && auth.user?.role !== 'ADMIN'
    && Array.isArray(auth.user?.modules)
    && auth.user.modules.length > 0
    && to.meta.requiresAuth
  ) {
    const ROUTE_TO_MODULE = {
      'dashboard': 'dashboard',
      'reports-food-cost': 'reports',
      'suppliers': 'suppliers',
      'purchase-orders': 'purchase-orders',
      'supplier-invoices': 'purchase-orders',
      'reorder': 'reorder',
      'ingredients': 'ingredients',
      'recipes': 'recipes',
      'production': 'production',
      'stock': 'stock',
      'stock-lots': 'stock-lots',
      'transfers': 'transfers',
      'stock-put-away': 'stock-put-away',
      'inventories': 'inventories',
      'stock-valuation': 'stock-valuation',
      'cashmag-dashboard': 'cashmag-dashboard',
      'cashmag-deferred': 'cashmag-dashboard',
      'cashmag-mapping': 'cashmag-mapping',
      'settings-haccp': 'haccp',
    };
    const moduleCode = ROUTE_TO_MODULE[to.name];
    // Seules les routes "module" sont gated (les pages settings/* personnelles
    // restent accessibles à tous les rôles). Le dashboard est le FALLBACK
    // universel des routes refusées ET la cible de `afterLogin()` : il ne doit
    // JAMAIS être bloqué, sinon un user sans le module 'dashboard' boucle
    // (dashboard → refus → redirection dashboard → …) et fige l'onglet.
    if (moduleCode && to.name !== 'dashboard' && !auth.user.modules.includes(moduleCode)) {
      return { name: 'dashboard' };
    }
  }
  return true;
});

export default router;
